import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {createPortal} from 'react-dom';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {selectTextAreaOpen} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {Button} from '~library/atoms/Button';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {largishAndUp} from '~libSass/base/_exports.sass';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import '../TextEditor.sass';

export const TextEditorControl = () => {
  const {canvas} = useContext(EventCreateContext);
  const [text, setText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const textAreaOpen = useSelector(selectTextAreaOpen);
  const dispatch = useDispatch();
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);

  useEffect(() => {
    const activeObject = canvas?.getActiveObject();
    setText(activeObject?.text);
    setOriginalText(activeObject?.text);
  }, [canvas, textAreaOpen]);

  useEffect(() => {
    const onTextEditingEntered = () =>
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {textAreaOpen: true},
      });

    canvas?.on({
      'text:editing:entered': onTextEditingEntered,
    });

    return () => {
      canvas?.off({
        'text:editing:entered': onTextEditingEntered,
      });
    };
  }, [canvas]);

  const changeText = (newText) => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject) {
      setText(newText);
      activeObject.text = newText;
    }
  };

  const onTextChangeHandler = useCallback(
    ({target: {value}}) => changeText(value),

    [canvas]
  );

  const closeEditor = () => {
    const activeObject = canvas?.getActiveObject();
    activeObject?.exitEditing?.();
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {textAreaOpen: false},
    });
    canvas.requestRenderAll();
  };

  const onClearClickHandler = () => changeText('');

  const onCancelClickHandler = () => {
    changeText(originalText);
    closeEditor();
  };

  const onDoneClickHandler = () => {
    changeText(text); // prevent automatic tests non save
    closeEditor();
  };

  const handleFocus = (event) => event.target.select();

  if (isDesktop || !textAreaOpen) {
    return null;
  }

  /* eslint-disable jsx-a11y/no-autofocus */
  return createPortal(
    <div
      className={`mobile_editor__text_editor ${
        textAreaOpen ? 'mobile_editor__text_editor--visible' : ''
      }`}
    >
      <div className="mobile_editor__text_editor__header">
        <Button
          data-qa-id="step1-texteditor-cancel"
          className="mobile_editor__text_editor__header__cancel_btn"
          variant="link"
          size="small"
          onClick={onCancelClickHandler}
        >
          Cancel
        </Button>
        <Button
          data-qa-id="step1-texteditor-clear"
          className="mobile_editor__text_editor__header__clear_btn"
          variant="link"
          size="small"
          onClick={onClearClickHandler}
        >
          Clear
        </Button>
        <Button
          data-qa-id="step1-texteditor-done"
          className="mobile_editor__text_editor__header__done_btn"
          variant="link"
          size="small"
          onClick={onDoneClickHandler}
        >
          Done
        </Button>
      </div>
      <div className="mobile_editor__text_editor__body">
        <textarea
          onFocus={handleFocus}
          onChange={onTextChangeHandler}
          value={text}
          autoFocus
          key={originalText}
          data-qa-id="step1-texteditor-input"
        />
      </div>
    </div>,
    document.getElementById('pbModalRoot')
  );
};
